/* eslint-disable prefer-destructuring */
import React from 'react';
import { Avatar, AvatarProps, SvgIconProps, Theme } from '@mui/material';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import Person from '@mui/icons-material/Person';
import {
  ContactGuestSchema,
  GuestSchema,
  UserGuestSchema,
} from '../../definitions/schemas';

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    backgroundColor: 'var(--avatar-background-default)',
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2.fontSize,
  },
  backgroundBlue: {
    backgroundColor: 'var(--avatar-background-blue)',
  },
  backgroundGreen: {
    backgroundColor: 'var(--avatar-background-green)',
  },
  backgroundLightBlue: {
    backgroundColor: 'var(--avatar-background-light-blue)',
  },
  backgroundOrange: {
    backgroundColor: 'var(--avatar-background-orange)',
  },
  backgroundSecondary: {
    backgroundColor: 'var(--secondary-color-background)',
  },
  backgroundWhite: {
    backgroundColor: theme.palette.background.paper,
  },
  colorSecondary: {
    color: theme.palette.secondary.main,
  },
  colorWhite: {
    color: theme.palette.background.paper,
  },
}));

export enum AvatarBackground {
  Blue = 'blue',
  Custom = 'custom',
  Green = 'green',
  LightBlue = 'lightBlue',
  Orange = 'orange',
  Secondary = 'secondary',
  White = 'white',
}

export enum AvatarColor {
  Secondary = 'secondary',
  White = 'white',
}

export interface GuestAvatarProps {
  anonymize?: boolean;
  background?: AvatarBackground;
  className?: string;
  color?: AvatarColor;
  guest: GuestSchema;
  displayOneInitialOnly?: boolean;
  icon?: React.ReactNode;
  iconProps?: SvgIconProps;
  style?: React.CSSProperties;
  sx?: AvatarProps['sx'];
}

const GuestAvatar: React.FC<GuestAvatarProps> = (
  props: GuestAvatarProps
): JSX.Element => {
  const classes = useStyles();

  const {
    anonymize,
    background,
    className,
    color,
    displayOneInitialOnly,
    guest,
    icon,
    iconProps,
    style,
    sx,
  } = props;

  const { avatarUri, mobilePhone } = guest as ContactGuestSchema;
  const { email, firstName, lastName, photo } = guest as UserGuestSchema;

  let initials = '';

  if (firstName) {
    initials = firstName[0];
    initials += lastName ? lastName[0] : firstName[1] || '';
  } else if (lastName) {
    initials = lastName[0];
    initials += lastName[1] || '';
  } else if (email) {
    const emailParts = email.split('@')[0];

    let parts = emailParts.split('.', 2);
    if (parts.length < 2) {
      parts = emailParts.split('+', 2);
    }

    const [head, tail] = parts;
    initials = head[0];
    initials += tail ? tail[0] : head[1] || '';
  }

  const renderAvatarIcon = (): React.ReactNode => {
    if (icon) {
      return icon;
    }

    if (initials && !anonymize) {
      let avatarInitials = initials.toUpperCase();
      if (displayOneInitialOnly) {
        avatarInitials = avatarInitials[0] || '';
      }
      return avatarInitials;
    }

    return mobilePhone || anonymize ? <Person {...iconProps} /> : undefined;
  };

  return (
    <Avatar
      className={classNames(classes.avatar, className, {
        [classes.backgroundBlue]: background === AvatarBackground.Blue,
        [classes.backgroundGreen]: background === AvatarBackground.Green,
        [classes.backgroundOrange]: background === AvatarBackground.Orange,
        [classes.backgroundLightBlue]:
          background === AvatarBackground.LightBlue,
        [classes.backgroundSecondary]:
          background === AvatarBackground.Secondary,
        [classes.backgroundWhite]: background === AvatarBackground.White,
        [classes.colorSecondary]: color === AvatarColor.Secondary,
        [classes.colorWhite]: color === AvatarColor.White,
      })}
      src={photo || avatarUri}
      style={style}
      sx={sx}
    >
      {renderAvatarIcon()}
    </Avatar>
  );
};

export default GuestAvatar;
